// Real HTTP adapter for FEATURE-031 (Disease Code Management) — talks to admin-api at
// /api/v1/admin/disease-groups + /disease-codes. Selected over the in-memory mock when
// VITE_USE_MOCK_API=false (see disease-code.adapter.ts); the production/docker env files set
// that flag, so this is the live deployment path.
// Maps the server error envelope { error: { code, message, fields, meta } } to DiseaseCodeApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  DiseaseCodeApiError,
  type CreateDiseaseCodeInput,
  type CreateDiseaseGroupInput,
  type DiseaseCode,
  type DiseaseCodeApi,
  type DiseaseCodeApiErrorCode,
  type DiseaseCodeReferences,
  type DiseaseGroup,
  type UpdateDiseaseCodeInput,
  type UpdateDiseaseGroupInput,
} from '../types/disease-code.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never got an HTTP reply — offline, DNS, or
  // timeout. Surface it as NETWORK (distinct message) instead of UNKNOWN so the
  // toast tells the user to check their connection rather than "unknown error".
  if (!candidate.response) {
    throw new DiseaseCodeApiError('NETWORK', '네트워크 연결을 확인한 후 다시 시도해주세요.');
  }
  const payload = candidate.response?.data?.error;
  throw new DiseaseCodeApiError(
    (payload?.code as DiseaseCodeApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const diseaseCodeHttpApi: DiseaseCodeApi = {
  async listGroups(): Promise<DiseaseGroup[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<DiseaseGroup[]>>(endpoints.diseaseGroups);
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new DiseaseCodeApiError('UNKNOWN', '질환군 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async createGroup(input: CreateDiseaseGroupInput): Promise<DiseaseGroup> {
    try {
      const res = await httpClient.post<ApiEnvelope<DiseaseGroup>>(
        endpoints.diseaseGroups,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateGroup(id: number, input: UpdateDiseaseGroupInput): Promise<DiseaseGroup> {
    try {
      const res = await httpClient.patch<ApiEnvelope<DiseaseGroup>>(
        `${endpoints.diseaseGroups}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteGroup(id: number): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.diseaseGroups}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async listCodes(groupId: number): Promise<DiseaseCode[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<DiseaseCode[]>>(
        `${endpoints.diseaseGroups}/${groupId}/codes`,
      );
      const data = res.data?.data;
      if (!Array.isArray(data)) {
        throw new DiseaseCodeApiError('UNKNOWN', '질환 코드 목록을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },

  async createCode(groupId: number, input: CreateDiseaseCodeInput): Promise<DiseaseCode> {
    try {
      const res = await httpClient.post<ApiEnvelope<DiseaseCode>>(
        `${endpoints.diseaseGroups}/${groupId}/codes`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateCode(id: number, input: UpdateDiseaseCodeInput): Promise<DiseaseCode> {
    try {
      const res = await httpClient.patch<ApiEnvelope<DiseaseCode>>(
        `${endpoints.diseaseCodes}/${id}`,
        input,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteCode(id: number): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.diseaseCodes}/${id}`);
    } catch (e) {
      mapError(e);
    }
  },

  async getCodeReferences(id: number): Promise<DiseaseCodeReferences> {
    try {
      const res = await httpClient.get<ApiEnvelope<DiseaseCodeReferences>>(
        `${endpoints.diseaseCodes}/${id}/references`,
      );
      const data = res.data?.data;
      // The reference panel maps over `proItems`/`deploymentModules`; the `?? []`
      // guard only catches a null field, so a wrong-shape body must throw here.
      if (
        typeof data !== 'object' ||
        data === null ||
        !Array.isArray(data.proItems) ||
        !Array.isArray(data.deploymentModules)
      ) {
        throw new DiseaseCodeApiError('UNKNOWN', '코드 참조 정보를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      mapError(e);
    }
  },
};
